# Whisper speech-to-text via Transformers.js
import ["pipeline"], from: '@xenova/transformers'
# Active Storage for audio file persistence
import ["initActiveStorage"], from: 'juntos:active-storage'
# Clip model for client-side persistence
import ["Clip"], from: 'juntos:models'

class DictaphoneController < Stimulus::Controller
  def connect
    @transcriber = nil
    @translator = nil
    @mediaRecorder = nil
    @audioContext = nil
    @analyser = nil
    @chunks = []
    @startTime = nil
    @timerInterval = nil
    @isRecording = false
    @audioBlob = nil      # Store audio blob for Active Storage
    @audioDuration = nil  # Store recording duration
    @rawAudioChunks = []  # Raw PCM samples for Whisper

    initStorage()
    loadModel()
  end

  def disconnect
    stopRecording() if @isRecording
    @timerInterval && clearInterval(@timerInterval)
  end

  # Initialize Active Storage
  async def initStorage
    begin
      await initActiveStorage()
    rescue => error
      console.error("Failed to initialize Active Storage:", error)
    end
  end

  # Load the Whisper model (downloads ~75MB on first use, cached after)
  async def loadModel
    begin
      @transcriber = await pipeline(
        'automatic-speech-recognition',
        'Xenova/whisper-tiny.en',
        progress_callback: ->(progress) {
          if progress.status == 'progress' && progress.progress
            pct = progress.progress.round
            statusTarget.textContent = "Loading Whisper model... #{pct}%"
          elsif progress.status == 'ready'
            statusTarget.textContent = "Model loaded!"
          end
        }
      )

      statusTarget.textContent = "Loading translation model..."

      @translator = await pipeline(
        'translation',
        'Xenova/opus-mt-en-es',
        progress_callback: ->(progress) {
          if progress.status == 'progress' && progress.progress
            pct = progress.progress.round
            statusTarget.textContent = "Loading translation model... #{pct}%"
          end
        }
      )

      statusTarget.textContent = "Ready to record"
      statusTarget.classList.remove('bg-blue-50', 'text-blue-800')
      statusTarget.classList.add('bg-green-50', 'text-green-800')
      recordTarget.disabled = false
    rescue => error
      console.error("Failed to load models:", error)
      statusTarget.textContent = "Failed to load models. Please refresh and try again."
      statusTarget.classList.remove('bg-blue-50', 'text-blue-800')
      statusTarget.classList.add('bg-red-50', 'text-red-800')
    end
  end

  def toggleRecording
    if @isRecording
      stopRecording()
    else
      startRecording()
    end
  end

  async def startRecording
    begin
      stream = await navigator.mediaDevices.getUserMedia(audio: true)

      # Set up audio analysis for visualizer
      @audioContext = AudioContext.new
      source = @audioContext.createMediaStreamSource(stream)
      @analyser = @audioContext.createAnalyser()
      @analyser.fftSize = 256
      source.connect(@analyser)

      # Capture raw PCM audio for Whisper (avoids decodeAudioData issues)
      @rawAudioChunks = []
      processor = @audioContext.createScriptProcessor(4096, 1, 1)
      processor.onaudioprocess = ->(e) {
        data = e.inputBuffer.getChannelData(0)
        @rawAudioChunks.push(Float32Array.new(data))
      }
      source.connect(processor)
      processor.connect(@audioContext.destination)
      @processor = processor

      # Determine best supported format
      mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ?
                   'audio/webm;codecs=opus' : 'audio/mp4'

      @mediaRecorder = MediaRecorder.new(stream, mimeType: mimeType)
      @chunks = []

      @mediaRecorder.ondataavailable = ->(e) {
        @chunks.push(e.data) if e.data
      }

      @mediaRecorder.onstop = -> { handleRecordingComplete() }

      @mediaRecorder.start(100) # Collect data every 100ms
      @isRecording = true
      @startTime = Date.now()

      # Update UI
      recordLabelTarget.textContent = "Stop"
      recordTarget.classList.remove('bg-red-600', 'hover:bg-red-700')
      recordTarget.classList.add('bg-gray-800', 'hover:bg-gray-900')
      timerTarget.classList.remove('hidden')
      visualizerTarget.classList.remove('hidden')
      previewTarget.classList.add('hidden')

      # Start timer and visualizer
      updateTimer()
      @timerInterval = setInterval(-> { updateTimer() }, 100)
      visualize()

    rescue => error
      console.error("Failed to start recording:", error)
      statusTarget.textContent = "Microphone access denied. Please allow microphone access."
      statusTarget.classList.remove('bg-green-50', 'text-green-800')
      statusTarget.classList.add('bg-red-50', 'text-red-800')
    end
  end

  def stopRecording
    return unless @mediaRecorder && @isRecording

    @mediaRecorder.stop()
    @mediaRecorder.stream.getTracks().each { |track| track.stop() }
    @isRecording = false

    clearInterval(@timerInterval) if @timerInterval
    @processor.disconnect() if @processor
    @sampleRate = @audioContext.sampleRate if @audioContext
    @audioContext.close() if @audioContext

    # Update UI
    recordLabelTarget.textContent = "Record"
    recordTarget.classList.remove('bg-gray-800', 'hover:bg-gray-900')
    recordTarget.classList.add('bg-red-600', 'hover:bg-red-700')
    timerTarget.classList.add('hidden')
    visualizerTarget.classList.add('hidden')
  end

  def updateTimer
    return unless @startTime
    elapsed = (Date.now() - @startTime) / 1000
    mins = Math.floor(elapsed / 60).toString().padStart(2, '0')
    secs = Math.floor(elapsed % 60).toString().padStart(2, '0')
    timerTarget.textContent = "#{mins}:#{secs}"
  end

  def visualize
    return unless @analyser && @isRecording

    dataArray = Uint8Array.new(@analyser.frequencyBinCount)
    @analyser.getByteFrequencyData(dataArray)

    # Calculate average level
    sum = 0
    dataArray.each { |value| sum += value }
    average = sum / dataArray.length
    percentage = Math.min(100, (average / 128) * 100)

    levelTarget.style.width = "#{percentage}%"

    requestAnimationFrame(-> { visualize() }) if @isRecording
  end

  def resampleAudio(chunks, fromRate, toRate)
    # Concatenate all chunks into a single Float32Array
    totalLength = 0
    chunks.each { |chunk| totalLength += chunk.length }
    combined = Float32Array.new(totalLength)
    offset = 0
    chunks.each do |chunk|
      combined.set(chunk, offset)
      offset += chunk.length
    end

    # Resample if rates differ
    if fromRate == toRate
      return combined
    end

    ratio = fromRate.to_f / toRate
    newLength = (combined.length / ratio).round
    result = Float32Array.new(newLength)
    i = 0
    while i < newLength
      result[i] = combined[(i * ratio).floor]
      i += 1
    end
    result
  end

  async def handleRecordingComplete
    # Create blob from chunks
    mimeType = @mediaRecorder.mimeType
    @audioBlob = Blob.new(@chunks, type: mimeType)
    @audioDuration = (Date.now() - @startTime) / 1000

    # Create playback URL
    audioUrl = URL.createObjectURL(@audioBlob)
    audioTarget.src = audioUrl

    # Set duration in form
    durationTarget.value = @audioDuration.toFixed(2)

    # Generate default name
    now = Date.new
    nameTarget.value = "Recording #{now.toLocaleDateString()} #{now.toLocaleTimeString()}"

    # Show preview
    previewTarget.classList.remove('hidden')

    # Start transcription
    transcribe(@audioBlob)
  end

  async def transcribe(blob)
    return unless @transcriber

    transcribingTarget.classList.remove('hidden')
    transcriptTarget.value = ""
    transcriptTarget.placeholder = "Transcribing..."

    begin
      # Combine raw PCM chunks and resample to 16kHz for Whisper
      audioData = resampleAudio(@rawAudioChunks, @sampleRate, 16000)

      # Transcribe
      result = await @transcriber.call(audioData)
      transcript = result.text.strip()

      transcriptTarget.value = transcript
      transcriptTarget.placeholder = "Transcription will appear here..."

      # Translate
      if @translator && transcript.length > 0
        translatingTarget.classList.remove('hidden')
        translation = await @translator.call(transcript)
        translationTarget.value = translation[0].translation_text
        translatingTarget.classList.add('hidden')
      end

    rescue => error
      console.error("Transcription failed:", error)
      transcriptTarget.placeholder = "Transcription failed. You can type manually."
    ensure
      transcribingTarget.classList.add('hidden')
    end
  end

  async def save(event)
    event.preventDefault()

    return unless @audioBlob

    begin
      # Create clip record first
      clip = await Clip.create(
        name: nameTarget.value || "Untitled Recording",
        transcript: transcriptTarget.value,
        translation: translationTarget.value,
        duration: parseFloat(durationTarget.value) || @audioDuration
      )

      # Attach audio via Active Storage
      extension = @audioBlob.type.include?('webm') ? 'webm' : 'm4a'
      await clip.audio.attach(@audioBlob,
        filename: "#{clip.name.gsub(/[^a-z0-9]/i, '_')}.#{extension}",
        content_type: @audioBlob.type
      )

      console.log("Clip saved with audio attachment:", clip.id)

      # Replace the initially-broadcast clip (which had no audio) with the full version
      await clip.broadcast_replace_to("clips")

      resetUI()

    rescue => error
      console.error("Failed to save clip:", error)
      statusTarget.textContent = "Failed to save clip. Please try again."
      statusTarget.classList.remove('bg-green-50', 'text-green-800')
      statusTarget.classList.add('bg-red-50', 'text-red-800')
    end
  end

  def discard
    resetUI()
  end

  def resetUI
    # Revoke object URL
    URL.revokeObjectURL(audioTarget.src) if audioTarget.src

    # Clear stored blob
    @audioBlob = nil
    @audioDuration = nil

    # Clear form
    audioTarget.src = ""
    audioDataTarget.value = ""
    durationTarget.value = ""
    nameTarget.value = ""
    transcriptTarget.value = ""
    translationTarget.value = ""

    # Hide preview
    previewTarget.classList.add('hidden')

    # Reset status
    statusTarget.textContent = "Ready to record"
    statusTarget.classList.remove('bg-red-50', 'text-red-800')
    statusTarget.classList.add('bg-green-50', 'text-green-800')
  end
end
